import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { createJobLead, updateJobLead, updateSalesContact, searchJobs } from '../../../api/sales';
import { useSelector } from 'react-redux';
import { formatDate, formatPhone, getUserInfoDetails, mapToOption, formatDateCST, formatTimeCST } from '../../../utlis';
import axiosInstance from '../../../api/axiosInstance';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import JobLeadsHeader from './components/JobLeadsHeader';
import JobLeadsTable from './components/JobLeadsTable';
import SalesContacts from '../Contacts/SalesContacts';
import SalesContactsModal from '../Contacts/components/SalesContactsModal';
import AssignJobModal from './components/AssignJobModal';
import { Modal } from '@mui/material';

const JobLeads = () => {
  // Get user context inside the component
  const { companyId, clientId } = useMemo(() => getUserInfoDetails(), []);
  const initialJobLead = {
    jobTitle: '',
    vendorId: '',
    durationMonths: '',
    client: '',
    technology: '',
    numOfPositions: '',
    sales_person_id: '',
    billRate: '',
    payType: '',
    state: '',
    city: '',
    createdOn: '',
    status: '',
    statusReason: '',
    jobContactName: '',
    jobContactEmail: '',
    jobContactPhoneNum: '',
    jobDescription: '',
  };
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({ mode: 'onChange' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [jobLead, setJobLead] = useState(initialJobLead);
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pageSize, setPageSize] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [backendResults, setBackendResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimerRef = useRef(null);

  // Filter state
  const [isFilterPopupOpen, setFilterPopupOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);

  const referenceData = useSelector((state) => state.reference);
  const [isContactsOpen, setIsContactsModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // Edit contact modal state
  const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
  const [editContactData, setEditContactData] = useState(null);

  // Assign Job modal state
  const [isAssignJobModalOpen, setIsAssignJobModalOpen] = useState(false);
  const [assignJobData, setAssignJobData] = useState(null);

  const handleContactSelect = (contact) => {
    setSelectedContact(contact); // Save the selected contact
    setIsContactsModalOpen(false); // Close the modal
  };

  useEffect(() => {
    if (selectedContact) {
      setValue('jobContactName', `${selectedContact.firstName} ${selectedContact.lastName}`, {
        shouldValidate: false,
        shouldDirty: false,
      });
      setValue('jobContactEmail', selectedContact.email, {
        shouldValidate: false,
        shouldDirty: false,
      });
      setValue('jobContactPhoneNum', selectedContact.mobileNum1, {
        shouldValidate: false,
        shouldDirty: false,
      });
      setValue('vendor_contact_id', selectedContact.contact_id, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [selectedContact, setValue]);

  // Reference data for modal dropdowns — fetched on demand when modal opens
  const [refSalesPerson, setRefSalesPerson] = useState(null);
  const [refVendor, setRefVendor] = useState(null);
  const [refTechnologies, setRefTechnologies] = useState(null);
  const [refSalesLoading, setRefSalesLoading] = useState(false);
  const [refVendorLoading, setRefVendorLoading] = useState(false);
  const [refTechnologiesLoading, setRefTechnologiesLoading] = useState(false);

  const fetchModalReferenceData = useCallback(async () => {
    const params = { company: companyId, client: clientId };
    if (!refSalesPerson) {
      setRefSalesLoading(true);
      axiosInstance.get('/salesteam', { params }).then(res => {
        setRefSalesPerson(res.data);
      }).catch(() => {}).finally(() => setRefSalesLoading(false));
    }
    if (!refVendor) {
      setRefVendorLoading(true);
      axiosInstance.get('/vendors', { params }).then(res => {
        setRefVendor(res.data);
      }).catch(() => {}).finally(() => setRefVendorLoading(false));
    }
    if (!refTechnologies) {
      setRefTechnologiesLoading(true);
      axiosInstance.get('/technology', { params }).then(res => {
        setRefTechnologies(res.data);
      }).catch(() => {}).finally(() => setRefTechnologiesLoading(false));
    }
  }, [companyId, clientId, refSalesPerson, refVendor, refTechnologies]);

  const openModalForNewJob = () => {
    setJobLead(initialJobLead);
    reset(initialJobLead); // Reset the form with initial values when opening for a new job
    fetchModalReferenceData();
    setIsModalOpen(true);
  };

  const openModalForUpdate = (jobLead) => {
    setJobLead(jobLead);
    fetchModalReferenceData();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setJobLead(initialJobLead);
    reset(initialJobLead); // Reset the form with initial values when closing the modal
  };

  const prepareUpdate = (job_id) => {
    const jobData = data.find((d) => d.job_id === job_id);
    console.log(jobData);
    if (!jobData) {
      toast.error('Job not found');
      return;
    }
    const createdDate = new Date(jobData.created);

    const currentJobLead = {
      jobTitle: jobData.job_title,
      jobId: jobData.job_id,
      vendorId: jobData.vendor_id,
      durationMonths: jobData.duration,
      client: jobData.client,
      technology: jobData.technology_id,
      numOfPositions: jobData.no_of_openings,
      sales_person_id: jobData.sales_person_id,
      billRate: jobData.bill_rate,
      payType: jobData.pay_type,
      state: jobData.state,
      city: jobData.city,
      createdOn: createdDate.toISOString().split('T')[0] || '',
      statusReason: jobData.status_reason || '',
      status: jobData.status || '',
      jobContactName: jobData.contact_name,
      jobContactEmail: jobData.contact_email,
      jobContactPhoneNum: jobData?.contact_phone?.mobile,
      jobDescription: jobData.job_desc,
      vendor_contact_id: jobData.vendor_contact_id,
      // Audit fields - store raw timestamps for formatting
      created: jobData.created || '',
      created_by: jobData.created_by || '',
      updated: jobData.updated || '',
      updated_by: jobData.updated_by || '',
    };
    setJobLead(currentJobLead);
    reset(currentJobLead, { keepDefaultValues: false });
    openModalForUpdate(currentJobLead); // Open the modal with the current job lead data
  };

  const handleFormSubmit = async (formData) => {
    try {
      const response = jobLead.jobId
        ? await updateJobLead({ ...formData, jobId: jobLead.jobId })
        : await createJobLead(formData);
      closeModal();
      getData();
      toast.success(
        jobLead.jobId ? 'Job Lead Updated Successfully' : 'Job Lead Created Successfully',
      );
    } catch (error) {
      toast.error('Failed to save job lead');
    }
  };

  const getData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/jobs', {
        params: { company: companyId, client: clientId, pageNum: 1, pageSize: 100 },
      });
      const responseData = res.data;
      if (!responseData?.data || !responseData.data.records) {
        const message = responseData?.message || 'Failed to load job leads';
        toast.error(message);
        setData([]);
        return;
      }
      setData(responseData.data.records);
    } catch (error) {
      toast.error('Something went wrong while fetching job data');
    } finally {
      setLoading(false);
    }
  };

  // Open edit contact modal — fetches contact on demand
  const openEditContactModal = async (vendorContactId) => {
    if (!vendorContactId) {
      toast.error('No contact associated with this job');
      return;
    }

    try {
      const res = await axiosInstance.get('/contacts', {
        params: { company: companyId, client: clientId },
      });
      const allContacts = res.data.data.records || [];
      const contact = allContacts.find((c) => c.contact_id === vendorContactId);

      if (!contact) {
        toast.error('Contact not found');
        return;
      }

      const createdDate = new Date(contact.created);
      const updatedDate = new Date(contact.updated);

      const contactData = {
        firstName: contact.first_name,
        middleName: contact.middle_name,
        lastName: contact.last_name,
        preferredName: contact.preferred_name,
        email: contact.email,
        jobTitle: contact.job_title,
        mobileNum1: contact?.phone?.mobile,
        mobileNum2: contact?.phone?.phone,
        fax: contact?.phone?.Fax || '',
        contactType: contact.contact_type || '',
        address1: contact.address?.address_line1,
        address2: contact.address?.address_line2,
        city: contact.address?.city,
        state: contact.address?.state,
        country: contact.address?.country,
        zipCode: contact.address?.zip,
        notes: contact.note || '',
        vendorStatus: contact.status || 'Active',
        contact_id: contact.contact_id,
        createdDate: createdDate.toISOString().split('T')[0] || '',
        createdTime: createdDate.toISOString().split('T')[1]?.slice(0, 5) || '',
        updateDate: updatedDate.toISOString().split('T')[0] || '',
        updateTime: updatedDate.toISOString().split('T')[1]?.slice(0, 5) || '',
        created_by: contact.created_by,
        updated_by: contact.updated_by,
      };

      setEditContactData(contactData);
      setIsEditContactModalOpen(true);
    } catch (error) {
      toast.error('Failed to load contact details. Please try again.');
    }
  };

  // Handle edit contact form submission
  const handleEditContactSubmit = async (formData) => {
    const payload = {
      divison: 'div',
      type: formData.contactType,
      firstName: formData.firstName,
      middleName: formData.middleName,
      lastName: formData.lastName,
      email: formData.email,
      preferredName: formData.preferredName,
      jobTitle: formData.jobTitle,
      phone: {
        mobile: formData.mobileNum1,
        phone: formData.mobileNum2,
        Fax: formData.fax,
      },
      address: {
        address_line2: formData.address2,
        address_line1: formData.address1,
        city: formData.city,
        state: formData.state,
        zip: formData.zipCode,
        country: formData.country,
      },
      status: formData.vendorStatus || 'Active',
      contact_id: formData.contact_id,
    };

    try {
      await updateSalesContact(payload);
      setIsEditContactModalOpen(false);
      setEditContactData(null);
      getData(); // Refresh jobs data to show updated contact info
      toast.success('Contact Updated Successfully');
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong while updating contact');
    }
  };

  // Assign Job handlers
  const handleAssignJobClick = (jobId) => {
    const jobRecord = data.find((d) => d.job_id === jobId);
    if (!jobRecord) {
      toast.error('Job not found');
      return;
    }
    setAssignJobData(jobRecord);
    fetchModalReferenceData();
    setIsAssignJobModalOpen(true);
  };

  const closeAssignJobModal = () => {
    setIsAssignJobModalOpen(false);
    setAssignJobData(null);
  };

  useEffect(() => {
    getData();
  }, []);

  // Backend search fallback — debounced
  const performBackendSearch = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setBackendResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await searchJobs(term);
      const records = response.data?.data?.records || [];
      setBackendResults(records);
    } catch (error) {
      setBackendResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Search and filter handlers
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    clearTimeout(searchTimerRef.current);

    if (!value.trim()) {
      setBackendResults([]);
      return;
    }

    searchTimerRef.current = setTimeout(() => {
      performBackendSearch(value.trim());
    }, 500);
  };

  const handleFilterClick = () => {
    setFilterPopupOpen(true);
  };

  const handleCloseFilterPopup = () => {
    setFilterPopupOpen(false);
  };

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters || []);
    setFilterPopupOpen(false);
  };

  // Filter search results for FilterPopup
  const getSearchResults = (searchTerm, selectedCategory) => {
    if (!searchTerm || !selectedCategory || !data) return [];

    return data
      .filter((item) => {
        let valueToSearch = '';

        switch (selectedCategory) {
          case 'Job Title':
            valueToSearch = item.job_title || '';
            break;
          case 'Vendor Name':
            valueToSearch = item.vendor_name || '';
            break;
          case 'Client':
            valueToSearch = item.client || '';
            break;
          case 'Duration':
            valueToSearch = item.duration?.toString() || '';
            break;
          case 'Bill Rate':
            valueToSearch = item.bill_rate?.toString() || '';
            break;
          case 'Sales Person':
            valueToSearch = item.sales_person_name || '';
            break;
          case 'Job Location':
            valueToSearch = item.city || '';
            break;
          case 'Technology':
            valueToSearch = item.technology_desc || '';
            break;
          default:
            valueToSearch = '';
        }

        return valueToSearch.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .map((item) => {
        switch (selectedCategory) {
          case 'Job Title':
            return item.job_title;
          case 'Vendor Name':
            return item.vendor_name;
          case 'Client':
            return item.client;
          case 'Duration':
            return item.duration?.toString();
          case 'Bill Rate':
            return item.bill_rate?.toString();
          case 'Sales Person':
            return item.sales_person_name;
          case 'Job Location':
            return item.city;
          case 'Technology':
            return item.technology_desc;
          default:
            return null;
        }
      })
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
  };

  // Apply search and filters to data
  useEffect(() => {
    if (!data.length) {
      setFilteredData([]);
      return;
    }

    let result = data;

    // Apply search filter first
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((item) => {
        return (
          (item.job_title || '').toLowerCase().includes(searchLower) ||
          (item.vendor_name || '').toLowerCase().includes(searchLower) ||
          (item.client || '').toLowerCase().includes(searchLower) ||
          (item.technology_desc || '').toLowerCase().includes(searchLower) ||
          (item.city || '').toLowerCase().includes(searchLower) ||
          (item.sales_person_name || '').toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply category filters
    if (Array.isArray(appliedFilters) && appliedFilters.length > 0) {
      result = result.filter((item) => {
        return appliedFilters.some(({ category, items }) => {
          if (!items || items.length === 0) return false;

          switch (category) {
            case 'Job Title':
              return items.some(
                (title) => title.toLowerCase() === (item.job_title || '').toLowerCase(),
              );
            case 'Vendor Name':
              return items.some(
                (name) => name.toLowerCase() === (item.vendor_name || '').toLowerCase(),
              );
            case 'Client':
              return items.some(
                (client) => client.toLowerCase() === (item.client || '').toLowerCase(),
              );
            case 'Duration':
              return items.some((duration) => duration === item.duration?.toString());
            case 'Bill Rate':
              return items.some((rate) => rate === item.bill_rate?.toString());
            case 'Sales Person':
              return items.some(
                (name) => name.toLowerCase() === (item.sales_person_name || '').toLowerCase(),
              );
            case 'Job Location':
              return items.some(
                (city) => city.toLowerCase() === (item.city || '').toLowerCase(),
              );
            case 'Technology':
              return items.some(
                (tech) => tech.toLowerCase() === (item.technology_desc || '').toLowerCase(),
              );
            default:
              return false;
          }
        });
      });
    }

    // If local search finds nothing, use backend results
    if (result.length === 0 && searchTerm.trim() && backendResults.length > 0) {
      const localIds = new Set(result.map((j) => j.job_id));
      result = [...result, ...backendResults.filter((j) => !localIds.has(j.job_id))];
    }

    setFilteredData(result);
  }, [appliedFilters, data, searchTerm, backendResults]);

  const filterCategories = [
    'Job Title',
    'Vendor Name',
    'Client',
    'Duration',
    'Bill Rate',
    'Sales Person',
    'Job Location',
    'Technology',
  ];

  const columns = [
    {
      name: 'job_id',
      label: 'ID',
      options: {
        display: [false],
      },
    },
    {
      name: 'contact_phone',
      label: 'contact_phone',
      options: {
        display: [false],
      },
    },
    {
      name: 'contact_name',
      label: 'contact_name',
      options: {
        display: [false],
      },
    },
    {
      name: 'vendor_contact_id',
      label: 'vendor_contact_id',
      options: {
        display: false,
      },
    },
    {
      name: 'contact_email',
      label: 'contact_email',
      options: {
        display: false,
      },
    },
        {
      name: 'job_title',
      label: 'Job Title',
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          const job_title = tableMeta.rowData[tableMeta.columnIndex];
          const job_desc = tableMeta.rowData[tableMeta.columnIndex + 1];
          // Always use the prepareUpdate handler directly
          return (
            <>
              <h3
                onClick={() => prepareUpdate(tableMeta.rowData[0])}
                className="text-base font-bold text-auxiliary-800 cursor-pointer"
              >
                {`${job_title}`}
              </h3>
              <p className="text-text-hint">{job_desc}</p>
            </>
          );
        },
      },
    },

    {
      name: 'vendor_name',
      label: 'Vendor Name',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'client',
      label: 'Client',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'duration',
      label: 'Duration',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => `${value} months`,
      },
    },
    {
      name: 'bill_rate',
      label: 'Bill Rate',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => `${value} USD`,
      },
    },
    {
      name: 'sales_person_name',
      label: 'Sales Person Name',
      options: {
        filter: true,
        sort: true,
      },
    },

    {
      name: 'city',
      label: 'Job Location',
    },
    {
      name: 'technology_desc',
      label: 'Technology Description',
    },
    {
      name: 'created',
      label: 'Created on',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => formatDate(value),
      },
    },

    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,

        customBodyRender: (value) => (
          <p className="py-2 rounded-full w-32 text-center text-secondary-500 activecolor">
            {value || 'N/A'}
          </p>
        ),
      },
    },
    {
      name: 'status',
      label: 'Job Contact',
      options: {
        filter: true,
        sort: true,

        customBodyRender: (value, tableMeta, updateValue) => {
          const contactName = tableMeta.rowData[2];
          const vendorContactId = tableMeta.rowData[3];
          const contactEmail = tableMeta.rowData[4] || 'N/A';
          const contactPhone = tableMeta.rowData[1]?.mobile;

          return (
            <div className="flex flex-col gap-1 py-1">
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  openEditContactModal(vendorContactId);
                }}
                className="text-sm font-semibold text-auxiliary-800 cursor-pointer hover:underline"
              >
                {contactName || 'N/A'}
              </div>
              <div className="text-xs text-text-hint">{contactEmail}</div>
              <div className="text-xs text-text-hint">{formatPhone(contactPhone) || 'N/A'}</div>
            </div>
          );
        },
      },
    },
    {
      name: 'job_id',
      label: ' ',
      options: {
        filter: false,
        sort: false,
        print: false,
        download: false,
        customBodyRender: (value, tableMeta) => {
          const jobId = tableMeta.rowData[0];
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleAssignJobClick(jobId);
              }}
              className="py-2 px-4 bg-secondary-700 text-white rounded-full hover:bg-secondary-800 text-sm whitespace-nowrap"
            >
              Assign Job
            </button>
          );
        },
      },
    },
    // {
    //   name: "created",
    //   label: "Jon Contact",
    //   options: {
    //     filter: true,
    //     sort: true,
    //   }
    // },
  ];
  const options = {
    filterType: 'dropdown',
    responsive: 'standard',
    rowsPerPage: pageSize,
    rowsPerPageOptions: [5, 10, 15, 20, 50],
    onChangeRowsPerPage: (numberOfRows) => setPageSize(numberOfRows),
    selectableRows: 'none',
    elevation: 0,
    filter: true,
    search: false, // We have our own search in header
    download: true,
    print: true,
    viewColumns: true,
    textLabels: {
      body: {
        noMatch: !isLoading && (!filteredData || filteredData.length === 0) ? 'Sorry, no matching found' : '',
      },
    },
  };
  return (
    <div className="p-5">
      <div className="border border-gray-300 rounded-lg relative">
        {/* Header */}
        <JobLeadsHeader
          onReload={getData}
          onAddJob={openModalForNewJob}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Backend search indicator */}
        {searchTerm.trim() && filteredData.length > 0 && backendResults.length > 0 && filteredData.some((j) => backendResults.find((b) => b.job_id === j.job_id)) && (
          <div className="px-4 py-2 bg-blue-50 text-blue-700 text-sm border-b border-blue-100">
            Showing results from server search for "{searchTerm}"
          </div>
        )}
        {searchTerm.trim() && isSearching && filteredData.length === 0 && (
          <div className="px-4 py-2 bg-gray-50 text-gray-500 text-sm border-b border-gray-100">
            Searching server...
          </div>
        )}

        {/* Table with loading overlay */}
        <div className="relative">
          <JobLeadsTable
            data={isLoading ? [] : filteredData}
            columns={columns}
            options={options}
            isFilterPopupOpen={isFilterPopupOpen}
            onFilterClick={handleFilterClick}
            onCloseFilterPopup={handleCloseFilterPopup}
            onApplyFilters={handleApplyFilters}
            getSearchResults={getSearchResults}
            filterCategories={filterCategories}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              {/* Spinner */}
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              {/* Loading text with animated dots */}
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>
      {/* Modal and Contacts Modal remain outside the table */}
      <div>
        <Modal open={isModalOpen} onClose={closeModal}>
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 w-full md:w-[1100px] max-w-5xl max-h-[90vh] overflow-y-auto border border-gray-200 relative">
              {/* Popup Title and Close Button */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-secondary-800">
                  {jobLead.jobId ? 'Edit Job Lead' : 'Add Job Lead'}
                </h2>
                <button
                  type="button"
                  onClick={closeModal}
                  className="text-gray-500 hover:text-secondary-800 text-2xl font-bold px-2"
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
              <form
                className="flex flex-col gap-4 md:gap-6"
                onSubmit={handleSubmit(handleFormSubmit)}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-10">
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="job_title" className="text-text-hint mb-1">
                      Job Title <span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('jobTitle', {
                        required: 'Job Title is required',
                      })}
                      name="jobTitle"
                      type="text"
                      placeholder="Job Title"
                      id="job_title"
                      className="outline-none border-2 border-secondary-500 rounded-lg pencialicon"
                    />
                    {errors.jobTitle && (
                      <span className="text-red-500">{errors.jobTitle.message}</span>
                    )}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="vendor" className="text-text-hint mb-1">
                      Vendor <span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('vendorId', {
                        required: 'Vendor is required',
                      })}
                      className="outline-none border-2 border-secondary-500 rounded-lg "
                    >
                      {!refVendorLoading &&
                        mapToOption(refVendor?.response?.data, 'name', 'vendor_id').map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.text}
                          </option>
                        ))}
                    </select>
                    {errors.vendorId && (
                      <span className="text-red-500">{errors.vendorId.message}</span>
                    )}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="duration" className="text-text-hint mb-1">
                      Duration in Months <span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('durationMonths', {
                        required: 'Duration Months is required',
                        pattern: { value: /^\d+$/, message: 'Must be a number' },
                      })}
                      placeholder="Duration Months"
                      id="durationMonths"
                      name="durationMonths"
                      type="text"
                      className="outline-none border-2 border-secondary-500 rounded-lg pencialicon"
                    />
                    {errors.durationMonths && (
                      <span className="text-red-500">{errors.durationMonths.message}</span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="client" className="text-text-hint mb-1">
                      Client <span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('client', { required: 'Client is required' })}
                      name="client"
                      type="text"
                      placeholder="Client "
                      id="client"
                      className="outline-none border-2 border-secondary-500 rounded-lg pencialicon"
                    />
                    {errors.client && <span className="text-red-500">{errors.client.message}</span>}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="technology" className="text-text-hint mb-1">
                      Technology <span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('technology', {
                        required: 'Technology is required',
                      })}
                      className="outline-none border-2 border-secondary-500 rounded-lg "
                    >
                      {!refTechnologiesLoading &&
                        mapToOption(
                          refTechnologies?.data?.records,
                          'technology_name',
                          'technology_id',
                        ).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.text}
                          </option>
                        ))}
                    </select>
                    {errors.technology && (
                      <span className="text-red-500">{errors.technology.message}</span>
                    )}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="client" className="text-text-hint mb-1">
                      Number of Positions <span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('numOfPositions', {
                        required: 'Number of Positions is required',
                        pattern: { value: /^\d+$/, message: 'Must be a number' },
                      })}
                      placeholder="Number of Positions"
                      type="text"
                      id="client"
                      className="outline-none border-2 border-secondary-500 rounded-lg pencialicon"
                    />
                    {errors.numOfPositions && (
                      <span className="text-red-500">{errors.numOfPositions.message}</span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="sales_manager" className="text-text-hint mb-1">
                      Sales Person <span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('sales_person_id', {
                        required: 'Sales Person is required',
                      })}
                      className="outline-none border-2 border-secondary-500 rounded-lg "
                    >
                      {!refSalesLoading &&
                        mapToOption(refSalesPerson?.data?.records, 'first_name', 'manager_id').map(
                          (option) => (
                            <option className="" key={String(option.value)} value={option.value}>
                              {option.text}
                            </option>
                          ),
                        )}
                    </select>
                    {errors.salesManager && (
                      <span className="text-red-500">{errors.salesManager.message}</span>
                    )}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="billRate" className="text-text-hint mb-1">
                      Bill Rate<span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('billRate', {
                        required: 'Bill Rate is required',
                        maxLength: {
                          value: 6, // 3 digits + 1 decimal + 2 decimal places = 6
                          message:
                            'Bill Rate must be 3 digits before decimal and 2 after decimal at most',
                        },
                        pattern: {
                          value: /^\d{1,3}(\.\d{1,2})?$/,
                          message:
                            'Must be a valid rate with up to 3 digits before decimal and up to 2 after decimal',
                        },
                      })}
                      placeholder="Bill Rate"
                      id="billRate"
                      type="text"
                      className="outline-none border-2 border-secondary-500 rounded-lg pencialicon"
                    />
                    {errors.billRate && (
                      <span className="text-red-500">{errors.billRate.message}</span>
                    )}
                  </div>

                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="pay_type" className="text-text-hint mb-1">
                      Pay Type<span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('payType', {
                        required: 'Pay Type is required',
                      })}
                      className="outline-none border-2 border-secondary-500 rounded-lg "
                    >
                      {mapToOption(referenceData.paytypes?.Pay, 'type').map((option) => (
                        <option className="" key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                    </select>
                    {errors.payType && (
                      <span className="text-red-500">{errors.payType.message}</span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="state" className="text-text-hint mb-1">
                      State<span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('state', { required: 'state  is required' })}
                      className="outline-none border-2 border-secondary-500 rounded-lg "
                    >
                      {mapToOption(referenceData?.states?.state, 'name').map((option) => (
                        <option className="" key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                    </select>
                    {errors.state && <span className="text-red-500">{errors.state.message}</span>}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="city" className="text-text-hint mb-1">
                      City<span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('city', { required: 'City is required' })}
                      className="outline-none border-2 border-secondary-500 rounded-lg "
                    >
                      {mapToOption(referenceData?.cities?.city, 'name').map((option) => (
                        <option className="" key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                    </select>
                    {errors.city && <span className="text-red-500">{errors.city.message}</span>}
                  </div>

                  {/* New button to navigate to contacts list */}
                  <div className="flex flex-col justify-end w-72">
                    <button
                      type="button"
                      onClick={() => setIsContactsModalOpen(true)}
                      className="bg-secondary-700 text-text-light outline-none border-2 border-secondary-500 rounded-full py-2 text-center"
                    >
                      Select Contact
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="status" className="text-text-hint mb-1">
                      Status <span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('status', { required: 'Status is required' })}
                      className="outline-none border-2 border-secondary-500 rounded-lg"
                    >
                      <option value="">Select Status</option>
                      {referenceData?.jobstatuses?.status && mapToOption(referenceData.jobstatuses.status, 'status').map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                    </select>
                    {errors.status && <span className="text-red-500">{errors.status.message}</span>}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 md:gap-8">
                  <div className="flex flex-col w-full space-y-1">
                    <label htmlFor="statusReason" className="text-text-hint mb-1">
                      Status Reason
                    </label>
                    <textarea
                      {...register('statusReason')}
                      name="statusReason"
                      placeholder="Status Reason"
                      id="statusReason"
                      className="outline-none border-2 border-secondary-500 rounded-lg w-full"
                      rows={4}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="job_contat" className="text-text-hint mb-1">
                      Contact Name<span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('jobContactName', {
                        required: 'Job Contact Name is required',
                      })}
                      placeholder="Job Contact Name"
                      type="text"
                      id="jobContactName"
                      disabled={true}
                      className="outline-none border-2 border-text-hint rounded-lg   disabled:border-gray-300 text-gray-300"
                    />
                    {errors.jobContactName && (
                      <span className="text-red-500">{errors.jobContactName.message}</span>
                    )}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="job_email" className="text-text-hint mb-1">
                      Contact Email<span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('jobContactEmail', {
                        required: 'Job Contact Email is required',
                        pattern: {
                          value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: 'Must be a valid email address',
                        },
                      })}
                      type="email"
                      placeholder="Job Contact Email"
                      id="jobContactEmail"
                      disabled={true}
                      className="outline-none border-2 border-text-hint rounded-lg   disabled:border-gray-300 text-gray-300"
                    />
                    {errors.jobContactEmail && (
                      <span className="text-red-500">{errors.jobContactEmail.message}</span>
                    )}
                  </div>
                  <div className="flex flex-col w-full md:w-72 space-y-1">
                    <label htmlFor="job_phone" className="text-text-hint mb-1">
                      Contact Phone Number
                      <span className="text-danger-500">*</span>
                    </label>
                    <input
                      {...register('jobContactPhoneNum', {
                        required: 'Job Contact Phone Number is required',
                      })}
                      placeholder="(999) 999 9999"
                      id="jobContactPhoneNum"
                      type="text"
                      disabled={true}
                      className="outline-none border-2 border-text-hint rounded-lg   disabled:border-gray-300 text-gray-300"
                    />
                    {errors.jobContactPhoneNum && (
                      <span className="text-red-500">{errors.jobContactPhoneNum.message}</span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 md:gap-8">
                  <div className="flex flex-col w-full space-y-1">
                    <label htmlFor="document_type" className="text-text-hint mb-1">
                      Job Description<span className="text-danger-500">*</span>
                    </label>
                    <textarea
                      {...register('jobDescription', {
                        required: 'Job Description is required',
                      })}
                      placeholder="Job Description"
                      id="jobDescription"
                      className="outline-none border-2 border-secondary-500 rounded-lg w-full"
                      rows={4}
                      // disabled={true}
                    />
                    {errors.jobDescription && (
                      <span className="text-red-500">{errors.jobDescription.message}</span>
                    )}
                  </div>
                </div>

                {/* Audit Fields - Only show in Edit Mode */}
                {jobLead.jobId && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
                    <div className="flex flex-col space-y-1">
                      <label className="text-text-hint text-xs">Created By</label>
                      <p className="text-sm text-gray-600">{jobLead.created_by || 'N/A'}</p>
                    </div>
                    <div className="flex flex-col space-y-1">
                      <label className="text-text-hint text-xs">Date Created</label>
                      <p className="text-sm text-gray-600">
                        {jobLead.created ? formatDateCST(jobLead.created) : 'N/A'}
                      </p>
                      {jobLead.created && (
                        <p className="text-xs text-gray-500">{formatTimeCST(jobLead.created)}</p>
                      )}
                    </div>
                    <div className="flex flex-col space-y-1">
                      <label className="text-text-hint text-xs">Updated By</label>
                      <p className="text-sm text-gray-600">{jobLead.updated_by || 'N/A'}</p>
                    </div>
                    <div className="flex flex-col space-y-1">
                      <label className="text-text-hint text-xs">Last Updated</label>
                      <p className="text-sm text-gray-600">
                        {jobLead.updated ? formatDateCST(jobLead.updated) : 'N/A'}
                      </p>
                      {jobLead.updated && (
                        <p className="text-xs text-gray-500">{formatTimeCST(jobLead.updated)}</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4">
                  <div className="flex flex-row-reverse justify-between items-center gap-3 w-full">
                    <button
                      type="submit"
                      disabled={!isValid || (jobLead.jobId && !isDirty)}
                      className={`py-2 px-6 rounded-lg shadow transition ${isValid && (!jobLead.jobId || isDirty) ? 'bg-secondary-700 text-text-light hover:bg-secondary-800 cursor-pointer' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="border border-secondary-800 text-secondary-800 py-2 px-6 rounded-lg hover:bg-gray-100 transition"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </Modal>

        {/* Contacts Modal for selecting a contact */}
        <Modal open={isContactsOpen} onClose={() => setIsContactsModalOpen(false)}>
          <div className="p-4">
            <SalesContacts onSelectContact={handleContactSelect} isSelectionMode={true} />
          </div>
        </Modal>

        {/* Edit Contact Modal */}
        <SalesContactsModal
          isOpen={isEditContactModalOpen}
          onClose={() => {
            setIsEditContactModalOpen(false);
            setEditContactData(null);
          }}
          onSubmit={handleEditContactSubmit}
          isEditMode={true}
          defaultValues={editContactData}
        />

        {/* Assign Job Modal */}
        {isAssignJobModalOpen && assignJobData && (
          <AssignJobModal
            isOpen={isAssignJobModalOpen}
            onClose={closeAssignJobModal}
            jobData={assignJobData}
            onSaveSuccess={getData}
            salesPersonData={refSalesPerson?.data?.records || []}
          />
        )}
      </div>
    </div>
  );
};

export default JobLeads;
